import React, { useState, Suspense, lazy } from 'react';
import { createBrowserRouter, Outlet, BrowserRouter as Router, RouterProvider } from 'react-router-dom';
import './styles/animation.css';
import './styles/BackToTop.css';
import './styles/responsive.css';

import { CustomToast } from './composants/toast/CustomToast';
import Background from './composants/background/background';
import { LoadingContext } from './composants/loadingContext';
import * as Utils from './composants/utils/utils';
import { LanguageContext } from './composants/LanguageContext';
import { IntlManager } from './IntlManager';
import CustomHeader from './components/header/customHeader';
import ListGallery from './page/gallery/list';
import ViewGallery from './page/gallery/view';
import Loader from './composants/loader/Loader';

// Lazy loaded components
const Login = lazy(() => import('./components/login/login'));
const ForgetPassword = lazy(() => import('./components/forgetPassword/forgetPassword'));
const ErrorPageTemplate = lazy(() => import('./page/error/404'));
const DashBoard = lazy(() => import('./page/dashboard/Dashboard'));
const ListParametres = lazy(() => import('./page/parametre/list/ListParametres'));
const ViewParametres = lazy(() => import('./page/parametre/view/ViewParametres'));
const UpdateParametre = lazy(() => import('./page/parametre/update/UpdateParametre'));
const ListUser = lazy(() => import('./page/user/list/ListUser'));
const ViewUser = lazy(() => import('./page/user/view/ViewUser'));
const UpdateUser = lazy(() => import('./page/user/update/UpdateUser'));
const ListDenonciation = lazy(() => import('./page/denonciation/list/ListDenonciation'));
const ViewDenonciation = lazy(() => import('./page/denonciation/view/ViewDenonciation'));
const ViewQRCode = lazy(() => import('./page/viewQRCode/ViewQRCode'));
const ListCertificate = lazy(() => import('./page/certificate/list/ListCertificate'));
const UpdateCertificate = lazy(() => import('./page/certificate/update/UpdateCertificate'));
const ListGroup = lazy(() => import('./page/groupe/list/ListGroup'));
const ViewGroup = lazy(() => import('./page/groupe/view'));
const ListGroupRoles = lazy(() => import('./page/groupe/view/ListGroupRoles'));
const UpdateGroup = lazy(() => import('./page/groupe/update'));
const ListGroupRolesAdd = lazy(() => import('./page/groupe/update/ListGroupRolesAdd'));
const ListArticle = lazy(() => import('./page/article/list'));
const ViewArticle = lazy(() => import('./page/article/view/ViewArticle'));
const UpdateArticle = lazy(() => import('./page/article/update'));
const ListAPropos = lazy(() => import('./page/apropos/list/ListAPropos'));
const ViewAPropos = lazy(() => import('./page/apropos/view/ViewAPropos'));
const UpdateAPropos = lazy(() => import('./page/apropos/update/UpdateAPropos'));
const ListTemoignage = lazy(() => import('./page/temoignage/list/ListTemoignage'));
const ViewTemoignage = lazy(() => import('./page/temoignage/view'));
const ListCentrePartenaire = lazy(() => import('./page/centrepartenaire/list'));
const ViewCentrePartenaire = lazy(() => import('./page/centrepartenaire/view'));
const ListSliders = lazy(() => import('./page/sliders/list'));
const ViewSlider = lazy(() => import('./page/sliders/view'));
const UpdateSlider = lazy(() => import('./page/sliders/update'));
const ListTheme = lazy(() => import('./page/themes/list/ListTheme'));
const ListMission = lazy(() => import('./page/mission/list'));
const ViewMission = lazy(() => import('./page/mission/view'));
const UpdateMission = lazy(() => import('./page/mission/update'));
const ListValeur = lazy(() => import('./page/valeur/list'));
const ViewValeur = lazy(() => import('./page/valeur/view'));
const UpdateValeur = lazy(() => import('./page/valeur/update'));
const ListSondages = lazy(() => import('./page/sondages/list/ListSondages'));
const ViewSondage = lazy(() => import('./page/sondages/view/ViewSondage'));
const SetResponseOfQuestion = lazy(() => import('./page/sondages/update/SetResponseOfQuestion'));
const AddArticle = lazy(() => import('./page/article/add'));
const GmailKit = lazy(() => import('./components/mail/GmailKit'));
const ListAlert = lazy(() => import('./page/alert/list/ListAlert'));
const Profile = lazy(() => import('./page/profile/profile'));
const ViewWeather = lazy(() => import('./page/weather/list/ViewWeather'));
const ListAlertType = lazy(() => import('./page/alertType/list/ListAlertType').then(m => ({ default: m.ListAlertType })));
const ListCategory = lazy(() => import('./page/category/list/ListCategory').then(m => ({ default: m.ListCategory })));
const ElearningDashboard = lazy(() => import('./page/elearning/dashboard/ElearningDashboard'));
const ModuleManagement = lazy(() => import('./page/elearning/modules/ModuleManagement'));
const ChapterManagement = lazy(() => import('./page/elearning/chapters/ChapterManagement'));
const PartenaireIndex = lazy(() => import('./page/partenaire'));
const DocumentManager = lazy(() => import('./page/document/DocumentManager'));


function App() {

    const route = createBrowserRouter([
        {
            path: '/',
            errorElement: <SignIn />,
            children: [
                {
                    path: '',
                    element: <SignIn />//<GmailKitSignIn />
                },
                {
                    path: 'login',
                    element: <SignIn />
                },
                {
                    path: 'forget_password',
                    element: <ForgetPWD />
                },
                {
                    path: 'admin',
                    element: <Root />,
                    children: [
                        {
                            path: 'dashboard',
                            element: <DashBoard />
                        },
                        //{
                        //    path: 'profile',
                        //    element: <Profile />
                        //},
                        {
                            path: 'parametre',
                            children: [
                                {
                                    path: '',
                                    element: <ListParametres />
                                },
                                {
                                    path: ':id',
                                    element: <ViewParametres />
                                },
                                {
                                    path: 'update/:id',
                                    element: <UpdateParametre />
                                }
                            ]
                        },
                        {
                            path: 'user',
                            children: [
                                {
                                    path: '',
                                    element: <ListUser />
                                },
                                {
                                    path: ':id',
                                    element: <ViewUser />
                                },
                                {
                                    path: 'update/:id',
                                    element: <UpdateUser />
                                }
                            ]
                        },
                        {
                            path: 'denonciation',
                            children: [
                                {
                                    path: '',
                                    element: <ListDenonciation />
                                },
                                {
                                    path: ':id',
                                    element: <ViewDenonciation />
                                }
                            ]
                        },
                        {
                            path: 'alert',
                            children: [
                                {
                                    path: '',
                                    element: <ListAlert />
                                }
                            ]
                        },
                        {
                            path: 'weather',
                            element: <ViewWeather />
                        },
                        {
                            path: 'risk',
                            element: <ListAlertType />
                        },
                        {
                            path: 'category',
                            element: <ListCategory />
                        },
                        {
                            path: 'qrcode',
                            element: <ViewQRCode />
                        },
                        {
                            path: 'certificate',
                            children: [
                                {
                                    path: '',
                                    element: <ListCertificate />
                                },
                                {
                                    path: 'update/:id',
                                    element: <UpdateCertificate />
                                }
                            ]
                        },
                        {
                            path: 'group',
                            children: [
                                {
                                    path: '',
                                    element: <ListGroup />
                                },
                                {
                                    path: ':id',
                                    element: <ViewGroup />,
                                    children: [
                                        {
                                            path: '',
                                            element: <></>
                                        },
                                        {
                                            path: 'role',
                                            element: <ListGroupRoles />
                                        }
                                    ]
                                },
                                {
                                    path: 'update',
                                    element: <UpdateGroup />,
                                    children: [
                                        {
                                            path: ':id',
                                            children: [
                                                {
                                                    path: '',
                                                    element: <></>
                                                },
                                                {
                                                    path: 'role',
                                                    element: <ListGroupRolesAdd />
                                                }
                                            ]
                                        }
                                    ]
                                }
                            ]
                        },
                        {
                            path: 'article',
                            children: [
                                {
                                    path: '',
                                    element: <ListArticle />
                                },
                                {
                                    path: 'ajouter',
                                    element: <AddArticle />
                                },
                                {
                                    path: ':id',
                                    element: <ViewArticle />
                                },
                                {
                                    path: 'update/:id',
                                    element: <UpdateArticle />
                                }
                            ]
                        },
                        {
                            path: 'apropos',
                            children: [
                                {
                                    path: '',
                                    element: <ListAPropos />
                                },
                                {
                                    path: ':id',
                                    element: <ViewAPropos />
                                },
                                {
                                    path: 'update/:id',
                                    element: <UpdateAPropos />
                                }
                            ]
                        },
                        {
                            path: 'temoignage',
                            children: [
                                {
                                    path: '',
                                    element: <ListTemoignage />
                                },
                                {
                                    path: ':id',
                                    element: <ViewTemoignage />
                                }
                            ]
                        },
                        {
                            path: 'usraj',
                            children: [
                                {
                                    path: '',
                                    element: <ListCentrePartenaire />
                                },
                                {
                                    path: ':id',
                                    element: <ViewCentrePartenaire />
                                }
                            ]
                        },
                        {
                            path: 'sliders',
                            children: [
                                {
                                    path: '',
                                    element: <ListSliders />
                                },
                                {
                                    path: ':id',
                                    element: <ViewSlider />
                                },
                                {
                                    path: 'update/:id',
                                    element: <UpdateSlider />
                                }
                            ]
                        },
                        {
                            path: 'gallery',
                            children: [
                                {
                                    path: '',
                                    element: <ListGallery />
                                },
                                {
                                    path: ':id',
                                    element: <ViewGallery />
                                }
                            ]
                        },
                        {
                            path: 'theme',
                            children: [
                                {
                                    path: '',
                                    element: <ListTheme />
                                }
                            ]
                        },
                        {
                            path: 'mission',
                            children: [
                                {
                                    path: '',
                                    element: <ListMission />
                                },
                                {
                                    path: ':id',
                                    element: <ViewMission />
                                },
                                {
                                    path: 'update/:id',
                                    element: <UpdateMission />
                                }
                            ]
                        },
                        {
                            path: 'valeur',
                            children: [
                                {
                                    path: '',
                                    element: <ListValeur />
                                },
                                {
                                    path: ':id',
                                    element: <ViewValeur />
                                },
                                {
                                    path: 'update/:id',
                                    element: <UpdateValeur />
                                }
                            ]
                        },
                        {
                            path: 'quizz',
                            children: [
                                {
                                    path: '',
                                    element: <ListSondages />
                                },
                                {
                                    path: ':id',
                                    element: <ViewSondage />
                                },
                                {
                                    path: 'update/:id',
                                    element: <SetResponseOfQuestion />
                                }
                            ]
                        },
                        {
                            path: 'learning',
                            children: [
                                {
                                    path: '',
                                    children: [
                                        {
                                            path: '',
                                            element: <ElearningDashboard />
                                        },
                                        {
                                            path: ':formationId/modules',
                                            element: <ModuleManagement />
                                        },
                                        {
                                            path: 'module',
                                            children: [
                                                {
                                                    path: ':moduleId/chapters',
                                                    element: <ChapterManagement />
                                                }
                                            ]
                                        },
                                    ]
                                }
                            ]
                        },
                        {
                            path: 'partenaire/*',
                            element: <PartenaireIndex />
                        },
                        /* {
                            path: 'documents',
                            element: <DocumentManager />
                        }, */
                        {
                            path: '*',
                            element: <ErrorPageTemplate code={404} />
                        }
                    ]
                }
            ]
        }
    ]);
    function Root() {
        const [loading, setLoading] = useState(true);
        const [langue, setLangue] = useState(Utils.TRADUCTION.fr);
        const valueLoading = {
            loading: loading,
            setLoading: setLoading
        };
        const valueLanguage = {
            langue: langue,
            setLangue: setLangue
        }

        return (
            <>
                {<>
                    <LoadingContext.Provider value={valueLoading}>
                        <LanguageContext.Provider value={valueLanguage}>
                            <IntlManager>
                                <CustomHeader>
                                    <Outlet />
                                </CustomHeader>
                            </IntlManager>
                        </LanguageContext.Provider>
                    </LoadingContext.Provider>

                </>}
            </>
        );
    }

    function SignIn() {
        const [langue, setLangue] = useState(Utils.TRADUCTION.fr);
        const [loading, setLoading] = useState(true);
        const valueLoading = {
            loading: loading,
            setLoading: setLoading
        }
        const valueLanguage = {
            langue: langue,
            setLangue: setLangue
        }
        return (
            <LoadingContext.Provider value={valueLoading}>
                <LanguageContext.Provider value={valueLanguage}>
                    <IntlManager>
                        <Background />
                        <CustomToast />
                        <Login />
                    </IntlManager>
                </LanguageContext.Provider>
            </LoadingContext.Provider>
        );
    }

    function ForgetPWD() {
        const [langue, setLangue] = useState(Utils.TRADUCTION.fr);
        const [loading, setLoading] = useState(true);
        const valueLoading = {
            loading: loading,
            setLoading: setLoading
        }
        const valueLanguage = {
            langue: langue,
            setLangue: setLangue
        }
        return (

            <LoadingContext.Provider value={valueLoading}>
                <LanguageContext.Provider value={valueLanguage}>
                    <IntlManager>
                        <Background />
                        <CustomToast />
                        <ForgetPassword />
                    </IntlManager>
                </LanguageContext.Provider>
            </LoadingContext.Provider>
        );
    }

    const PageLoading = () => (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100%' }}>
            <Loader />
        </div>
    );

    return (
        <Suspense fallback={<PageLoading />}>
            <RouterProvider router={route} />
        </Suspense>
    );
}

export default App;

